/**
 * Player (phone) view — designed as a slick mobile betting app.
 *
 *   - A "purse" wallet header (coin balance, name, current leg).
 *   - A turn area: when it's your move, three tactile action buttons (roll /
 *     leg bet / race bet) that expand into pickers; otherwise a calm waiting
 *     card with a live rankings strip.
 *   - A persistent "slips & cards" tray: your held leg-bet slips and your
 *     race-bet card hand.
 *
 * Move dispatch goes through the optional `onMove` prop (dev harnesses) or
 * the Firestore `applyMove` service in production.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { CamelUp, type RacingColor, type Room } from '@mdev/common'
import { CU, CU_DISPLAY, CU_HEADING } from '../theme'
import { CamelGlyph } from './CamelGlyph'
import { applyMove as applyCamelUpMove } from '../service'
import { LegReviewPanel } from './LegReviewPanel'
import { BettingSlip, Coins, Panel, PanelTitle, RaceCard } from './pieces'
import {
  GameNotActiveError,
  IllegalMoveError,
  RoomNotInGameError,
} from '../../lobby/errors'
import type { CamelUpSettings, CamelUpState } from '../module'

type Move = CamelUp.Move

interface Props {
  room: Room<CamelUpSettings>
  state: CamelUpState
  uid: string
  onMove?: (move: Move) => Promise<void>
}

type Picker = 'menu' | 'leg-bet' | 'race-bet'

export function PlayerGameView(props: Props) {
  const { room, state, uid, onMove } = props
  const me = state.players[uid]
  const isMyTurn = state.status === 'active' && state.seatOrder[state.activeIndex] === uid
  const [picker, setPicker] = useState<Picker>('menu')
  const [racePile, setRacePile] = useState<'winner' | 'loser'>('winner')
  const [moveError, setMoveError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const heldRaceColors = useMemo<RacingColor[]>(
    () => (me ? [...me.availableRaceCards] : []),
    [me],
  )

  const dispatchMove = useCallback(
    async (move: Move) => {
      setMoveError(null)
      setSubmitting(true)
      try {
        if (onMove) await onMove(move)
        else await applyCamelUpMove({ code: room.code, move })
        setPicker('menu')
      } catch (err) {
        if (
          err instanceof IllegalMoveError ||
          err instanceof GameNotActiveError ||
          err instanceof RoomNotInGameError
        ) {
          setMoveError(err.message)
        } else if (err instanceof Error) {
          setMoveError(err.message)
        } else {
          setMoveError(String(err))
        }
      } finally {
        setSubmitting(false)
      }
    },
    [onMove, room.code],
  )

  if (!me) {
    return (
      <div style={{ background: CU.pageBg, minHeight: '100%', padding: 20 }}>
        <span style={{ fontFamily: CU_DISPLAY, fontSize: 28, color: CU.ink }}>Camel Up</span>
        <p style={{ color: CU.inkSoft }}>You're not in this game.</p>
      </div>
    )
  }

  return (
    <div
      style={{
        background: CU.pageBg,
        minHeight: '100%',
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        maxWidth: 520,
        margin: '0 auto',
      }}
    >
      <Wallet me={me} leg={state.leg} />

      {moveError && (
        <div
          style={{
            padding: '10px 14px',
            borderRadius: 12,
            background: CU.dangerBg,
            border: `1px solid ${CU.dangerBorder}`,
            color: CU.ink,
            fontSize: 14,
          }}
        >
          {moveError}
        </div>
      )}

      {state.status === 'leg_end' && (
        <>
          <LegReviewPanel state={state} />
          <BigButton
            label="Start next leg"
            tone="gold"
            loading={submitting}
            onClick={() => dispatchMove({ kind: 'continue-leg', uid })}
          />
        </>
      )}

      {state.status === 'active' && !isMyTurn && <WaitingCard state={state} />}

      {state.status === 'active' && isMyTurn && picker === 'menu' && (
        <Panel pad={14} style={{ boxShadow: CU.shadow, borderColor: CU.accent }}>
          <PanelTitle style={{ color: CU.accent }}>Your move</PanelTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <BigButton
              label="Roll the pyramid"
              sub="Move a camel · +1 coin"
              tone="primary"
              icon={<PyramidIcon />}
              loading={submitting}
              onClick={() => dispatchMove({ kind: 'take-pyramid-ticket', uid })}
            />
            <BigButton
              label="Place a leg bet"
              sub="Back a camel for this leg"
              tone="papyrus"
              icon={<SlipIcon />}
              onClick={() => setPicker('leg-bet')}
            />
            <BigButton
              label="Bet on the race"
              sub={
                heldRaceColors.length
                  ? 'Pick the overall winner or loser'
                  : 'No race cards left'
              }
              tone="papyrus"
              icon={<CardIcon />}
              disabled={heldRaceColors.length === 0}
              onClick={() => setPicker('race-bet')}
            />
          </div>
        </Panel>
      )}

      {state.status === 'active' && isMyTurn && picker === 'leg-bet' && (
        <Panel pad={16} style={{ boxShadow: CU.shadow }}>
          <PanelTitle>Take a leg-bet slip</PanelTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            {CamelUp.RACING_COLORS.map((c) => {
              const stack = state.legBetStacks[c] ?? []
              const next = stack[0]
              const gone = stack.length === 0
              return (
                <div key={c} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <BettingSlip
                    color={c}
                    value={next ? next.value : 0}
                    disabled={gone || submitting}
                    onClick={
                      gone || submitting
                        ? undefined
                        : () => dispatchMove({ kind: 'take-leg-bet', uid, color: c })
                    }
                  />
                  <span style={{ fontSize: 11, color: CU.inkSoft, fontFamily: CU_HEADING }}>
                    {gone ? 'gone' : `${stack.length} left`}
                  </span>
                </div>
              )
            })}
          </div>
          <BackButton onClick={() => setPicker('menu')} />
        </Panel>
      )}

      {state.status === 'active' && isMyTurn && picker === 'race-bet' && (
        <Panel pad={16} style={{ boxShadow: CU.shadow }}>
          <PanelTitle>Bet on the overall race</PanelTitle>
          <Segmented
            value={racePile}
            onChange={setRacePile}
            options={[
              { value: 'winner', label: '🥇 Winner' },
              { value: 'loser', label: '🐌 Loser' },
            ]}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
            {heldRaceColors.map((c) => (
              <RaceCard
                key={c}
                color={c}
                onClick={
                  submitting
                    ? undefined
                    : () => dispatchMove({ kind: 'place-race-bet', uid, pile: racePile, color: c })
                }
              />
            ))}
          </div>
          <span style={{ fontSize: 12, color: CU.inkSoft, display: 'block', marginBottom: 12 }}>
            Tap a camel to place it face-down on the {racePile} pile. Correct guesses pay 8 / 5 / 3 /
            2 / 1 by order; wrong ones cost a coin.
          </span>
          <BackButton onClick={() => setPicker('menu')} />
        </Panel>
      )}

      <SlipsTray me={me} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Wallet header
// ---------------------------------------------------------------------------

function Wallet({ me, leg }: { me: CamelUpState['players'][string]; leg: number }) {
  return (
    <div
      style={{
        borderRadius: 18,
        padding: 16,
        background: 'linear-gradient(160deg, #fdf3da, #f0dcab)',
        border: `1px solid ${CU.border}`,
        boxShadow: CU.shadow,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* brass top rule */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${CU.coinDeep}, ${CU.coin}, ${CU.coinDeep})`,
        }}
      />
      {/* The wallet card is always a light papyrus surface, so its text is
          fixed dark-on-papyrus rather than theme-dependent (otherwise it
          would vanish in dark mode). */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#fff',
              border: '2px solid #b5791f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
            }}
          >
            {me.avatar}
          </span>
          <span style={{ fontFamily: CU_HEADING, fontWeight: 600, fontSize: 18, color: '#3a2a14' }}>
            {me.displayName}
          </span>
        </span>
        <span
          style={{
            fontFamily: CU_HEADING,
            fontSize: 11,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#9a6a1a',
            border: '1px solid #c69133',
            borderRadius: 999,
            padding: '3px 10px',
          }}
        >
          Leg {leg}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: CU_HEADING, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8a6a3a' }}>
          Your purse
        </span>
        <Coins amount={me.coins} big size={20} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Waiting card (not your turn)
// ---------------------------------------------------------------------------

function WaitingCard({ state }: { state: CamelUpState }) {
  const activeUid = state.seatOrder[state.activeIndex]
  const active = state.players[activeUid]
  const rankings = CamelUp.rankRacingCamels(state)
  return (
    <Panel pad={16}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: CU.accent,
            animation: 'cuPulse 1.4s ease-in-out infinite',
          }}
        />
        <span style={{ fontFamily: CU_HEADING, fontSize: 16, color: CU.ink }}>
          Waiting on {active?.avatar} <strong>{active?.displayName}</strong>
        </span>
      </div>
      <PanelTitle style={{ marginBottom: 8 }}>The field</PanelTitle>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {rankings.map((c, i) => (
          <span
            key={c}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px 4px 6px',
              borderRadius: 999,
              background: CU.surfaceSolid,
              border: `1px solid ${i === 0 ? CU.brass : CU.borderSoft}`,
            }}
          >
            <span style={{ fontFamily: CU_HEADING, fontWeight: 900, fontSize: 13, color: i === 0 ? CU.brass : CU.inkSoft }}>
              {i + 1}
            </span>
            <CamelGlyph color={c} size={18} noShadow />
          </span>
        ))}
      </div>
      {/* keyframes (scoped global) */}
      <style>{'@keyframes cuPulse{0%,100%{opacity:1}50%{opacity:0.25}}'}</style>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Slips & cards tray (always visible)
// ---------------------------------------------------------------------------

function SlipsTray({ me }: { me: CamelUpState['players'][string] }) {
  const placed = CamelUp.RACING_COLORS.length - me.availableRaceCards.length
  return (
    <Panel pad={16}>
      <PanelTitle>Your betting slips</PanelTitle>
      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4, marginBottom: 16, minHeight: 8 }}>
        {me.legBets.length === 0 ? (
          <span style={{ fontSize: 13, color: CU.inkSoft }}>No active slips this leg.</span>
        ) : (
          me.legBets.map((t, i) => <BettingSlip key={i} color={t.color} value={t.value} />)
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <PanelTitle style={{ marginBottom: 0 }}>Your race cards</PanelTitle>
        <span style={{ fontSize: 12, color: CU.inkSoft, fontFamily: CU_HEADING }}>{placed} placed</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {me.availableRaceCards.length === 0 ? (
          <span style={{ fontSize: 13, color: CU.inkSoft }}>All cards committed.</span>
        ) : (
          me.availableRaceCards.map((c) => <RaceCard key={c} color={c} size="sm" />)
        )}
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Buttons + bits
// ---------------------------------------------------------------------------

interface BigButtonProps {
  label: string
  sub?: string
  tone: 'primary' | 'gold' | 'papyrus'
  icon?: ReactNode
  onClick?: () => void
  disabled?: boolean
  loading?: boolean
}

function BigButton({ label, sub, tone, icon, onClick, disabled, loading }: BigButtonProps) {
  const palette =
    tone === 'primary'
      ? { bg: `linear-gradient(160deg, ${CU.accent}, ${CU.accentDeep})`, fg: '#fff', border: CU.accentDeep }
      : tone === 'gold'
        ? { bg: `linear-gradient(160deg, ${CU.coin}, ${CU.coinDeep})`, fg: '#3a2a14', border: CU.coinDeep }
        : { bg: CU.surfaceSolid, fg: CU.ink, border: CU.border }
  const isDisabled = disabled || loading
  return (
    <button
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        textAlign: 'left',
        padding: '14px 16px',
        borderRadius: 14,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        boxShadow: tone === 'papyrus' ? CU.shadowSoft : '0 4px 14px rgba(60,36,10,0.3)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.5 : 1,
        font: 'inherit',
        transition: 'transform 100ms ease, box-shadow 100ms ease',
      }}
    >
      {icon && <span style={{ display: 'flex', flex: '0 0 auto' }}>{icon}</span>}
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span style={{ fontFamily: CU_HEADING, fontWeight: 600, fontSize: 17 }}>
          {loading ? 'Working…' : label}
        </span>
        {sub && <span style={{ fontSize: 12, opacity: 0.85 }}>{sub}</span>}
      </span>
    </button>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        color: CU.inkSoft,
        fontFamily: CU_HEADING,
        fontSize: 14,
        cursor: 'pointer',
        padding: '4px 0',
      }}
    >
      ← Back
    </button>
  )
}

interface SegmentedProps {
  value: 'winner' | 'loser'
  onChange: (v: 'winner' | 'loser') => void
  options: Array<{ value: 'winner' | 'loser'; label: string }>
}

function Segmented({ value, onChange, options }: SegmentedProps) {
  return (
    <div
      style={{
        display: 'flex',
        background: CU.surfaceSolid,
        border: `1px solid ${CU.border}`,
        borderRadius: 12,
        padding: 4,
        gap: 4,
      }}
    >
      {options.map((o) => {
        const sel = o.value === value
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            style={{
              flex: 1,
              padding: '8px 0',
              borderRadius: 9,
              border: 'none',
              cursor: 'pointer',
              fontFamily: CU_HEADING,
              fontWeight: 600,
              fontSize: 14,
              background: sel ? CU.accent : 'transparent',
              color: sel ? '#fff' : CU.inkSoft,
              transition: 'background 120ms ease',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// Inline action icons (kept tiny + on-theme).

function PyramidIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <path d="M12 3 L21 21 H3 Z" fill="#fff" opacity="0.9" />
      <path d="M12 3 L12 21 H3 Z" fill="#fff" opacity="0.55" />
    </svg>
  )
}

function SlipIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="3" width="14" height="18" rx="2" fill={CU.brass} />
      <line x1="5" y1="9" x2="19" y2="9" stroke="#fff" strokeDasharray="2 2" strokeWidth="1.4" />
      <line x1="8" y1="13" x2="16" y2="13" stroke="#fff" strokeWidth="1.4" />
      <line x1="8" y1="16" x2="14" y2="16" stroke="#fff" strokeWidth="1.4" />
    </svg>
  )
}

function CardIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="5" width="11" height="15" rx="2" fill={CU.brassSoft} transform="rotate(-8 9 12)" />
      <rect x="9" y="4" width="11" height="15" rx="2" fill={CU.brass} transform="rotate(8 14 12)" />
    </svg>
  )
}
